// ─────────────────────────────────────────────────────────────────────────────
//  CHARIKATY AI COPILOT — MULTILINGUAL STRINGS
//  Supports: FR (default) | EN | AR (RTL)
// ─────────────────────────────────────────────────────────────────────────────

export type CopilotLang = 'fr' | 'en' | 'ar';

export interface CopilotStrings {
  // Launcher
  buttonLabel: string;
  // Header
  title: string;
  subtitle: string;
  // States
  welcomeTitle: string;
  welcomeSubtitle: string;
  typingLabel: string;
  placeholder: string;
  // Actions
  sendLabel: string;
  clearLabel: string;
  minimizeLabel: string;
  closeLabel: string;
  // Quick actions
  quickActionsTitle: string;
  actions: {
    dashboard: string;
    createCompany: string;
    documents: string;
    finance: string;
    genvest: string;
    settings: string;
  };
  // Context responses per module
  contextResponses: Record<string, string>;
  // Navigation
  navigatingTo: string;
  goButton: string;
  // Error
  errorMessage: string;
}

const fr: CopilotStrings = {
  buttonLabel: 'Ouvrir l\'assistant IA',
  title: 'Copilot Charikaty',
  subtitle: 'Assistant IA · Toujours disponible',
  welcomeTitle: 'Bonjour ! Je suis votre Copilot.',
  welcomeSubtitle: 'Je peux vous guider sur la plateforme, expliquer les fonctionnalités et vous aider à naviguer.',
  typingLabel: 'Le copilot rédige une réponse…',
  placeholder: 'Posez votre question…',
  sendLabel: 'Envoyer',
  clearLabel: 'Effacer',
  minimizeLabel: 'Réduire',
  closeLabel: 'Fermer',
  quickActionsTitle: 'Actions rapides',
  actions: {
    dashboard: 'Tableau de bord',
    createCompany: 'Créer une entreprise',
    documents: 'Mes documents',
    finance: 'Outils financiers',
    genvest: 'Investissement',
    settings: 'Paramètres',
  },
  contextResponses: {
    '/dashboard': 'Vous êtes sur le **Tableau de bord principal**. Depuis ici, vous pouvez accéder à tous les modules : Entreprises, Finance, Genvest et Infrastructure.',
    '/dashboard/companies': 'Vous êtes dans le module **Entreprises**. Vous pouvez créer une société, vérifier un nom commercial, gérer vos documents légaux ou consulter votre tableau de conformité.',
    '/dashboard/companies/creer-entreprise': 'Vous êtes sur le formulaire de **Création d\'entreprise**. Remplissez les informations requises pour générer votre dossier de création.',
    '/dashboard/companies/validation-marque': 'Module de **Validation de marque**. Saisissez un nom pour vérifier sa disponibilité et obtenir un rapport complet.',
    '/dashboard/companies/documents': 'Coffre-fort **Documents légaux**. Vous pouvez consulter, télécharger ou générer vos statuts et autres documents officiels.',
    '/dashboard/companies/gestion': 'Module de **Gestion d\'entreprise**. Suivez les indicateurs de performance, la gouvernance et les alertes de votre société.',
    '/dashboard/companies/conformite': 'Module **Conformité & Réglementation**. Consultez votre registre de conformité et l\'intelligence réglementaire.',
    '/dashboard/finance': 'Module **Finance**. Accédez à la comptabilité, la trésorerie, les taxes, les rapports financiers et les KPIs.',
    '/dashboard/finance/comptabilite': 'Vous êtes en **Comptabilité**. Gérez vos transactions, journaux comptables et bilans.',
    '/dashboard/finance/tresorerie': 'Module **Trésorerie**. Visualisez vos flux de trésorerie, prévisions et positions de liquidités.',
    '/dashboard/finance/indicateurs': 'Tableau de bord **Indicateurs KPI**. Saisissez vos données financières pour obtenir une analyse complète.',
    '/dashboard/finance/advisor': 'Votre **Conseiller Financier IA**. Obtenez des recommandations personnalisées basées sur vos données.',
    '/dashboard/finance/partenaires': 'Réseau de **Partenaires Financiers**. Explorez les partenaires disponibles et postulez au réseau.',
    '/dashboard/genvest': 'Module **Genvest — Investissement**. Gérez vos portefeuilles, simulez des risques et explorez les opportunités.',
    '/dashboard/genvest/portefeuille': 'Module **Portefeuille**. Suivez la performance de vos investissements et la répartition de vos actifs.',
    '/dashboard/genvest/ai-predictive': 'IA **Prédictive d\'investissement**. Analysez les tendances et obtenez des prédictions basées sur l\'intelligence artificielle.',
    '/dashboard/genvest/opportunites': 'Hub **Opportunités**. Découvrez les meilleures opportunités d\'investissement sélectionnées par notre IA.',
    '/dashboard/infrastructure': 'Module **Infrastructure Souveraine**. Gérez vos bases de données, la sécurité, la connectivité et la reprise après sinistre.',
    '/dashboard/settings': 'Page **Paramètres**. Configurez votre profil, les notifications et les préférences de la plateforme.',
    'default': 'Je suis votre assistant Charikaty. Comment puis-je vous aider ? Utilisez les actions rapides ou posez-moi une question sur la plateforme.',
  },
  navigatingTo: 'Navigation vers',
  goButton: 'Y aller →',
  errorMessage: 'Une erreur s\'est produite. Veuillez réessayer.',
};

const en: CopilotStrings = {
  buttonLabel: 'Open AI Assistant',
  title: 'Charikaty Copilot',
  subtitle: 'AI Assistant · Always available',
  welcomeTitle: 'Hello! I\'m your Copilot.',
  welcomeSubtitle: 'I can guide you through the platform, explain features and help you navigate to what you need.',
  typingLabel: 'Copilot is typing a response…',
  placeholder: 'Ask me anything…',
  sendLabel: 'Send',
  clearLabel: 'Clear',
  minimizeLabel: 'Minimize',
  closeLabel: 'Close',
  quickActionsTitle: 'Quick Actions',
  actions: {
    dashboard: 'Dashboard',
    createCompany: 'Create Company',
    documents: 'My Documents',
    finance: 'Finance Tools',
    genvest: 'Investment',
    settings: 'Settings',
  },
  contextResponses: {
    '/dashboard': 'You are on the **Main Dashboard**. From here you can access all modules: Companies, Finance, Genvest and Infrastructure.',
    '/dashboard/companies': 'You are in the **Companies** module. You can create a company, check a business name, manage legal documents or view your compliance dashboard.',
    '/dashboard/companies/creer-entreprise': 'You are on the **Company Creation** form. Fill in the required information to generate your incorporation file.',
    '/dashboard/companies/validation-marque': '**Brand Validation** module. Enter a name to check its availability and get a full report.',
    '/dashboard/companies/documents': '**Legal Document Vault**. View, download or generate your statutes and official documents.',
    '/dashboard/companies/gestion': '**Company Management** module. Track performance indicators, governance and alerts for your company.',
    '/dashboard/companies/conformite': '**Compliance & Regulation** module. View your compliance register and regulatory intelligence.',
    '/dashboard/finance': '**Finance** module. Access accounting, treasury, taxes, financial reports and KPIs.',
    '/dashboard/finance/comptabilite': 'You are in **Accounting**. Manage your transactions, ledgers and balance sheets.',
    '/dashboard/finance/tresorerie': '**Treasury** module. Visualize your cash flows, forecasts and liquidity positions.',
    '/dashboard/finance/indicateurs': '**KPI Indicators** dashboard. Enter your financial data to get a full analysis.',
    '/dashboard/finance/advisor': 'Your **AI Financial Advisor**. Get personalized recommendations based on your data.',
    '/dashboard/finance/partenaires': '**Financial Partners** network. Explore available partners and apply to the network.',
    '/dashboard/genvest': '**Genvest — Investment** module. Manage portfolios, simulate risks and explore opportunities.',
    '/dashboard/genvest/portefeuille': '**Portfolio** module. Track investment performance and asset allocation.',
    '/dashboard/genvest/ai-predictive': '**Predictive AI** for investment. Analyze trends and get AI-based predictions.',
    '/dashboard/genvest/opportunites': '**Opportunities** hub. Discover top investment opportunities selected by our AI.',
    '/dashboard/infrastructure': '**Sovereign Infrastructure** module. Manage databases, security, connectivity and disaster recovery.',
    '/dashboard/settings': '**Settings** page. Configure your profile, notifications and platform preferences.',
    'default': 'I am your Charikaty assistant. How can I help you? Use the quick actions or ask me anything about the platform.',
  },
  navigatingTo: 'Navigating to',
  goButton: 'Go there →',
  errorMessage: 'An error occurred. Please try again.',
};

const ar: CopilotStrings = {
  buttonLabel: 'فتح المساعد الذكي',
  title: 'مساعد شاريكاتي',
  subtitle: 'مساعد ذكي · متاح دائماً',
  welcomeTitle: 'مرحباً! أنا مساعدك الذكي.',
  welcomeSubtitle: 'يمكنني توجيهك عبر المنصة، شرح الميزات ومساعدتك في التنقل.',
  typingLabel: 'المساعد يكتب رداً…',
  placeholder: 'اسألني أي شيء…',
  sendLabel: 'إرسال',
  clearLabel: 'مسح',
  minimizeLabel: 'تصغير',
  closeLabel: 'إغلاق',
  quickActionsTitle: 'إجراءات سريعة',
  actions: {
    dashboard: 'لوحة التحكم',
    createCompany: 'إنشاء شركة',
    documents: 'وثائقي',
    finance: 'الأدوات المالية',
    genvest: 'الاستثمار',
    settings: 'الإعدادات',
  },
  contextResponses: {
    '/dashboard': 'أنت على **لوحة التحكم الرئيسية**. من هنا يمكنك الوصول إلى جميع الوحدات: الشركات والمالية وجينفيست والبنية التحتية.',
    '/dashboard/companies': 'أنت في وحدة **الشركات**. يمكنك إنشاء شركة أو التحقق من اسم تجاري أو إدارة الوثائق القانونية أو عرض لوحة الامتثال.',
    '/dashboard/companies/creer-entreprise': 'أنت في نموذج **إنشاء الشركة**. املأ المعلومات المطلوبة لإنشاء ملف التأسيس.',
    '/dashboard/companies/validation-marque': 'وحدة **التحقق من العلامة التجارية**. أدخل اسماً للتحقق من توفره والحصول على تقرير كامل.',
    '/dashboard/companies/documents': '**خزنة الوثائق القانونية**. اعرض أو حمّل أو أنشئ النظام الأساسي والوثائق الرسمية.',
    '/dashboard/companies/gestion': 'وحدة **إدارة الشركة**. تابع مؤشرات الأداء والحوكمة والتنبيهات.',
    '/dashboard/companies/conformite': 'وحدة **الامتثال والتنظيم**. اعرض سجل الامتثال والذكاء التنظيمي.',
    '/dashboard/finance': 'وحدة **المالية**. الوصول إلى المحاسبة والخزينة والضرائب والتقارير المالية ومؤشرات الأداء.',
    '/dashboard/finance/comptabilite': 'أنت في **المحاسبة**. إدارة معاملاتك ودفاترك وميزانياتك.',
    '/dashboard/finance/tresorerie': 'وحدة **الخزينة**. تصور تدفقاتك النقدية والتوقعات وأوضاع السيولة.',
    '/dashboard/finance/indicateurs': 'لوحة **مؤشرات الأداء الرئيسية**. أدخل بياناتك المالية للحصول على تحليل كامل.',
    '/dashboard/finance/advisor': '**المستشار المالي الذكي**. احصل على توصيات مخصصة بناءً على بياناتك.',
    '/dashboard/finance/partenaires': 'شبكة **الشركاء الماليين**. استكشف الشركاء المتاحين وتقدم إلى الشبكة.',
    '/dashboard/genvest': 'وحدة **جينفيست — الاستثمار**. إدارة المحافظ ومحاكاة المخاطر واستكشاف الفرص.',
    '/dashboard/genvest/portefeuille': 'وحدة **المحفظة**. تتبع أداء الاستثمار وتوزيع الأصول.',
    '/dashboard/genvest/ai-predictive': '**الذكاء الاصطناعي التنبؤي** للاستثمار. تحليل الاتجاهات والحصول على تنبؤات قائمة على الذكاء الاصطناعي.',
    '/dashboard/genvest/opportunites': 'مركز **الفرص**. اكتشف أفضل فرص الاستثمار التي اختارها ذكاؤنا الاصطناعي.',
    '/dashboard/infrastructure': 'وحدة **البنية التحتية السيادية**. إدارة قواعد البيانات والأمان والاتصال والتعافي من الكوارث.',
    '/dashboard/settings': 'صفحة **الإعدادات**. ضبط ملفك الشخصي والإشعارات وتفضيلات المنصة.',
    'default': 'أنا مساعدك في شاريكاتي. كيف يمكنني مساعدتك؟ استخدم الإجراءات السريعة أو اسألني أي شيء عن المنصة.',
  },
  navigatingTo: 'الانتقال إلى',
  goButton: '← الذهاب',
  errorMessage: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
};

export const copilotTranslations: Record<CopilotLang, CopilotStrings> = { fr, en, ar };

/** Given a pathname, returns the best matching context key */
export function getBestContextKey(pathname: string, lang: CopilotLang): string {
  const responses = copilotTranslations[lang].contextResponses;
  // Try exact match first, then progressive prefix match
  if (responses[pathname]) return pathname;
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length; i > 0; i--) {
    const key = '/' + segments.slice(0, i).join('/');
    if (responses[key]) return key;
  }
  return 'default';
}
